import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import SearchField from '../../../shared/components/SearchField';
import useIsNarrowViewport from '../../../shared/hooks/useIsNarrowViewport';

// The strip above a list: search, then that list's own filters, then what the filters left.
//
// Lifted out of DataTable so the media library's grid can carry the same bar. A grid and a
// table are different ways of showing a list, and the controls over them should not be.
//
// Below `md` the filters fold away behind the search box. A phone has room for one control, and
// the one that earns it is search: picking a workflow type and a tag scope on a 390px screen is
// not what anyone opens a list to do, while typing three letters is. The filters are a tap away
// rather than gone, behind a labelled toggle so it is clear there is more than search here.
export default function ListFilterBar({
  value,
  onChange,
  placeholder,
  inputId,
  extras,
  summary,
  className,
  extrasLabel,
}) {
  const isNarrow = useIsNarrowViewport();
  const [extrasOpen, setExtrasOpen] = useState(false);
  const extrasId = useId();

  // Expanded, the filters sit in the bar as they always have. Collapsed is a narrow-screen
  // state only, so resizing a desktop window never hides a filter the user is mid-way through.
  const foldExtras = isNarrow && Boolean(extras);

  return (
    <div className={`mb-3 ${className}`}>
      <div className="d-flex flex-wrap gap-2 align-items-end">
        <SearchField
          id={inputId}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="flex-grow-1"
          style={extras ? { minWidth: 200, maxWidth: 360 } : { maxWidth: 320 }}
        />

        {foldExtras ? (
          <button
            type="button"
            className="btn btn-light border d-flex align-items-center gap-1"
            onClick={() => setExtrasOpen(open => !open)}
            aria-expanded={extrasOpen}
            aria-controls={extrasId}
            title={extrasOpen ? 'Hide the extra filters' : 'Show the extra filters'}
          >
            <i className="bi bi-funnel" />
            <span className="small">More filters</span>
            <i className={`bi ${extrasOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
          </button>
        ) : (
          extras
        )}

        {/* What the filters left, e.g. "7 of 12 documents" — it answers the question the filters
            raise, so it sits with them rather than under the list. */}
        {summary && <div className="ms-auto text-muted small pb-2">{summary}</div>}
      </div>

      {foldExtras && (
        <div id={extrasId} hidden={!extrasOpen}>
          <fieldset className="border rounded p-3 mt-2 mb-0">
            <legend className="float-none w-auto px-1 mb-0 small text-muted">
              {extrasLabel}
            </legend>
            <div className="d-flex flex-column gap-2">{extras}</div>
          </fieldset>
        </div>
      )}
    </div>
  );
}

ListFilterBar.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  inputId: PropTypes.string,
  extras: PropTypes.node,
  summary: PropTypes.node,
  className: PropTypes.string,
  /** Heading for the folded filter box, for a list whose filters want naming. */
  extrasLabel: PropTypes.string,
};

ListFilterBar.defaultProps = {
  placeholder: 'Search…',
  inputId: undefined,
  extras: null,
  summary: null,
  className: '',
  extrasLabel: 'More filters',
};
